import { useEffect, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import Input from "../blocks/Input";
import NotFound from "./notFound";

import Button from "../blocks/Button";
import {
  hasValidationErrors,
  validateEmail,
  validatePassword,
  validateText,
  validatePasswordConfirm,
  validateRegisterForm,
} from "../../utils/validation";
import Snackbar from "../blocks/Snackbar";
import PhoneInput from "../blocks/PhoneInput";
import { usePhoneInput } from "../../hooks/usePhone";
import { useCountry } from "../../hooks/useCountry";
import Heading from "../blocks/Heading";
import VerificationCodeInput from "./Verify";

const Register = () => {
  const {
    register,
    isAuthenticated,
    isLoading,
    error,
    clearError,
    confirmEmail,
    requiresEmailConfirmation,
    setIsRedirecting,
    isRedirecting,
  } = useAuth();
  const { country: detectedCountry, loading: countryLoading } = useCountry();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [defaultCountry, setDefaultCountry] = useState("ae");
  const [stage, setStage] = useState(0);

  useEffect(() => {
    if (!countryLoading && detectedCountry) {
      setDefaultCountry(detectedCountry.toLowerCase());
      console.log("Country updated to:", detectedCountry);
    }
  }, [countryLoading, detectedCountry]);

  const [touched, setTouched] = useState<Record<string, boolean>>({
    firstName: false,
    lastName: false,
    email: false,
    phone: false,
    password: false,
    confirmPassword: false,
  });

  const [errors, setErrors] = useState<Record<string, string | null>>({
    email: null,
    firstName: null,
    lastName: null,
    phone: null,
    password: null,
    confirmPassword: null,
  });

  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    phone,
    setPhone,
    country,
    setCountry,
    isValid: isPhoneValid,
    error: phoneError,
    getSubmitValue,
  } = usePhoneInput({
    defaultCountry: defaultCountry,
    onPhoneChange: (_formattedValue, _countryValue) => {
      if (touched.phone && phoneError) {
        setErrors((prev) => ({ ...prev, phone: null }));
      }
    },
  });

  const handlePhoneChange = (value: string) => {
    setPhone(value);
  };

  const handlePhoneCountryChange = (country: any) => {
    setCountry(country);
  };

  const handlePhoneBlur = () => {
    setTouched((prev) => ({ ...prev, phone: true }));

    if (!phone) {
      setErrors((prev) => ({ ...prev, phone: "Phone number is required" }));
    } else if (!isPhoneValid) {
      setErrors((prev) => ({
        ...prev,
        phone: `Invalid phone for ${country?.name}.`,
      }));
    } else {
      setErrors((prev) => ({ ...prev, phone: null }));
    }
  };

  const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFirstName(value);
    if (touched.firstName && !validateText(value, "First Name")) {
      setErrors((prev) => ({ ...prev, firstName: null }));
    }
  };

  const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLastName(value);
    if (touched.lastName && !validateText(value, "Last Name")) {
      setErrors((prev) => ({ ...prev, lastName: null }));
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    if (touched.email && !validateEmail(value)) {
      setErrors((prev) => ({ ...prev, email: null }));
    }
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    if (touched.password) {
      const newError = validatePassword(value);
      setErrors((prev) => ({ ...prev, password: newError }));
    }
  };

  const handlePasswordConfirmChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (touched.confirmPassword) {
      const newError = validatePasswordConfirm(password, value);
      setErrors((prev) => ({ ...prev, confirmPassword: newError }));
    }
  };

  const handleFirstNameBlur = () => {
    setTouched((prev) => ({ ...prev, firstName: true }));
    const newErrors = validateText(firstName, "First Name");
    setErrors((prev) => ({ ...prev, firstName: newErrors }));
  };

  const handleLastNameBlur = () => {
    setTouched((prev) => ({ ...prev, lastName: true }));
    const newErrors = validateText(lastName, "Last Name");
    setErrors((prev) => ({ ...prev, lastName: newErrors }));
  };

  const handleEmailBlur = () => {
    setTouched((prev) => ({ ...prev, email: true }));
    const newErrors = validateEmail(email);
    setErrors((prev) => ({ ...prev, email: newErrors }));
  };

  const handlePasswordBlur = () => {
    setTouched((prev) => ({ ...prev, password: true }));
    const newErrors = validatePassword(password);
    setErrors((prev) => ({ ...prev, password: newErrors }));
  };

  const handleConfirmPasswordBlur = () => {
    setTouched((prev) => ({ ...prev, confirmPassword: true }));
    const newErrors = validatePasswordConfirm(password, confirmPassword);
    setErrors((prev) => ({ ...prev, confirmPassword: newErrors }));
  };

  const checkRateLimit = (): boolean => {
    if (loginAttempts >= 5) {
      setIsRateLimited(true);
      setTimeout(() => {
        setLoginAttempts(0);
        setIsRateLimited(false);
      }, 60000);
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (requiresEmailConfirmation) {
      setStage(1);
    }
  }, [requiresEmailConfirmation]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    setTouched({
      email: true,
      password: true,
      confirmPassword: true,
      phone: true,
      firstName: true,
      lastName: true,
    });

    if (!checkRateLimit()) {
      setErrors({
        email: "Too many sign-up attempts. Please try again in 1 minute.",
        password: null,
        phone: null,
        firstName: null,
        lastName: null,
        confirmPassword: null,
      });
      return;
    }

    let phoneValidationError: string | null = null;
    if (!phone) {
      phoneValidationError = "Phone number is required";
    }

    const newErrors = validateRegisterForm({
      email,
      password,
      firstName,
      lastName,
      phone: phone || "",
      confirmPassword,
    });

    newErrors.phone = phoneValidationError;
    setErrors(newErrors);

    if (hasValidationErrors(newErrors)) return;

    try {
      setLoginAttempts((prev) => prev + 1);

      const phoneForAPI = getSubmitValue();
      if (!phoneForAPI) {
        setErrors((prev) => ({
          ...prev,
          phone: "Invalid phone number format",
        }));
        return;
      }

      await register(email, password, firstName, phoneForAPI, lastName);
      setStage(1);
    } catch (err) {
      console.error("Sign up error:", err);
    }
  };

  // ✅ FIXED: Resend sign-up code
  const handleResendCode = async () => {
    clearError();
    setSuccessMessage(null);

    try {
      setIsRateLimited(true);

      // Use Amplify's resendSignUpCode to resend verification code
      const { resendSignUpCode } = await import("aws-amplify/auth");
      await resendSignUpCode({ username: email });

      setSuccessMessage("Verification code resent. Check your email!");
    } catch (err) {
      const authErr = err as any;
      let errorMessage = "Failed to resend verification code";

      if (authErr?.name === "LimitExceededException") {
        errorMessage = "Too many attempts. Please try again later.";
      } else if (authErr?.name === "UserNotFoundException") {
        errorMessage = "User not found. Please sign up again.";
      } else if (authErr?.message) {
        errorMessage = authErr.message;
      }

      setSuccessMessage(null);
      // You might want to set this to an error state or use a Snackbar
      console.error("Resend code error:", err);
      throw new Error(errorMessage);
    } finally {
      setIsRateLimited(false);
    }
  };

  // ✅ FIXED: Guard against NotFound while redirecting
  if (isAuthenticated && !isRedirecting) {
    return <NotFound />;
  }

  if (isAuthenticated && isRedirecting) {
    return (
      <div className="auth__global__wrapper">
        <div className="authentication__component">
          <div className="auth_wrapper">
            <Heading
              heading="Registration successful"
              text="Email confirmed successfully. Redirecting to your profile..."
            />
            <p className="authentication__subtext">
              Please wait while we redirect you. If nothing happens,{" "}
              <a href="/profile">click here</a>.
            </p>
          </div>
          <img
            src="https://cdn.prod.website-files.com/6710c34695543e421832e7c4/691d7434a0d32a2cf8494eb9_image.png"
            className="authentication__image"
          />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (stage === 0) {
      return (
        <div className="auth__global__wrapper">
          <div className="authentication__component">
            <div className="auth_wrapper">
              <Heading
                heading="Create your account"
                text={
                  <>Get started on the web, explore everything in the app!</>
                }
              />

              <form onSubmit={handleSubmit}>
                <div className="authentication__register">
                  <div className="authentication__section">
                    <p className="authentication__heading">Personal Details</p>
                    <div className="authentication__grid">
                      {error && (
                        <Snackbar text={error} cleanErrorTimeout={clearError} />
                      )}

                      <Input
                        id="firstName-input"
                        name="First name"
                        type="text"
                        value={firstName}
                        onChange={handleFirstNameChange}
                        onBlur={handleFirstNameBlur}
                        error={errors.firstName || undefined}
                        isValid={!errors.firstName && firstName.length > 0}
                        disabled={isLoading || isRateLimited}
                        required
                      />

                      <Input
                        id="lastName-input"
                        name="Last name"
                        type="text"
                        value={lastName}
                        onChange={handleLastNameChange}
                        onBlur={handleLastNameBlur}
                        error={errors.lastName || undefined}
                        isValid={!errors.lastName && lastName.length > 0}
                        disabled={isLoading || isRateLimited}
                        required
                      />
                    </div>

                    <div className="authentication__grid">
                      <Input
                        id="email-input"
                        name="Email"
                        type="email"
                        value={email}
                        onChange={handleEmailChange}
                        onBlur={handleEmailBlur}
                        error={errors.email || undefined}
                        isValid={!errors.email && email.length > 0}
                        disabled={isLoading || isRateLimited}
                        required
                      />

                      <PhoneInput
                        id="phone-input"
                        value={phone}
                        onChange={handlePhoneChange}
                        onBlur={handlePhoneBlur}
                        error={errors.phone || undefined}
                        isValid={isPhoneValid && phone.length > 0}
                        disabled={isLoading || isRateLimited}
                        required={true}
                        defaultCountry={defaultCountry}
                        onCountryChange={handlePhoneCountryChange}
                        ariaLabel="Phone number"
                        placeholder="Enter your phone number"
                      />
                    </div>
                  </div>

                  <div className="authentication__section">
                    <p className="authentication__heading">Security Details</p>
                    <div className="authentication__grid">
                      <Input
                        id="password-input"
                        name="Password"
                        type="password"
                        value={password}
                        onChange={handlePasswordChange}
                        onBlur={handlePasswordBlur}
                        error={errors.password || undefined}
                        isValid={!errors.password && password.length > 0}
                        disabled={isLoading || isRateLimited}
                        required
                      />

                      <Input
                        id="confirm-password-input"
                        name="Confirm password"
                        type="password"
                        value={confirmPassword}
                        onChange={handlePasswordConfirmChange}
                        onBlur={handleConfirmPasswordBlur}
                        error={errors.confirmPassword || undefined}
                        isValid={
                          !errors.confirmPassword && confirmPassword.length > 0
                        }
                        disabled={isLoading || isRateLimited}
                        required
                      />
                    </div>

                    <label className="checkbox__wrapper" htmlFor="checkbox">
                      <input
                        type="checkbox"
                        className="checkbox"
                        name="checkbox"
                        id="checkbox"
                        required
                      />
                      <p className="checkbox_text">
                        By creating account, I agree to&nbsp;
                        <a className="checkbox__link" href="/privacy-policy">
                          Terms and Conditions & Privacy Policy
                        </a>
                        .
                      </p>
                    </label>
                  </div>
                  <div>
                    <Button
                      disabled={
                        isLoading ||
                        isRateLimited ||
                        hasValidationErrors(errors) ||
                        !email ||
                        !password ||
                        !confirmPassword ||
                        !firstName ||
                        !lastName ||
                        !isPhoneValid
                      }
                      text={isLoading ? "Creating account..." : "Sign up"}
                    />
                  </div>
                </div>
              </form>
            </div>
            <img
              src="https://cdn.prod.website-files.com/6710c34695543e421832e7c4/691d7434a0d32a2cf8494eb9_image.png"
              className="authentication__image"
            />
          </div>
        </div>
      );
    }

    if (stage === 1) {
      return (
        <div>
          <div className="auth__global__wrapper">
            <div className="authentication__component">
              {successMessage && (
                <Snackbar
                  text={successMessage}
                  cleanErrorTimeout={() => setSuccessMessage(null)}
                />
              )}
              <VerificationCodeInput
                email={email}
                heading="Enter security code"
                description={
                  <>
                    We've sent a security code to your registered e-mail address
                    ({email}).
                    <br />
                    Please open your inbox and enter the security code to
                    confirm your account.
                    <br />
                    <br />
                    Check your spam or promotions folder – sometimes it hides
                    there.
                    <br />
                    Still nothing? You can resend the code below.
                  </>
                }
                onVerify={async (code: string) => {
                  setSuccessMessage(
                    "Email confirmed successfully. Redirecting to your profile..."
                  );
                  setIsRedirecting(true);

                  setTimeout(async () => {
                    try {
                      await confirmEmail(code);
                    } catch (err) {
                      setSuccessMessage(null);
                      setIsRedirecting(false);
                      console.error("Verification error:", err);
                    }
                  }, 500);
                }}
                onResend={handleResendCode}
                isLoading={isLoading || isRateLimited}
                showResendButton={true}
              />
              <img
                src="https://cdn.prod.website-files.com/6710c34695543e421832e7c4/691d7434a0d32a2cf8494eb9_image.png"
                className="authentication__image"
              />
            </div>
          </div>
        </div>
      );
    }
  }
};

export default Register;
